import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Loader,
  ErrorFallback,
  TextInput,
  FormMultiSelect,
  buildNotification,
  GenerateNotification,
  isValidate,
  isFromValid,
  InputSwitch,
  DropDownInput,} from 'smart-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, CardHeader, CardBody } from '@progress/kendo-react-layout';
import {
  Button,
  SplitButton,
  SplitButtonItem,
} from '@progress/kendo-react-buttons';

import { Form, Field } from '@progress/kendo-react-form';
import { Error } from '@progress/kendo-react-labels';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import {
  createIntegrationSystem,
  getIntegrationSystem,
  getIntegrationTagAssignments,
  saveIntegrationTagAssignments,
  updateIntegrationSystem,
  filterIntegrationTags,
} from '../Services/IntegrationSystemService';
import {
  EVENTS_DATA_TYPES,
  NOTIFICATION_TYPES,
} from '../../../../constants/eventDataTypes';
import { useAuth } from '../../../Core/Context/AuthContext';
import DesktopLayout from '../Components/Layout/DesktopLayout';
import TenantDropdown from '../../../Core/Components/TenantDropdown/TenantDropdown';
import NoRecordFound from '../../../Core/Components/NoRecordFound/NoRecordFound';
import useQueryParams from '../../../Core/CustomHooks/useQueryParams';
import {
  base64ToObject,
  objectToBase64,
} from '../../../../Utils/Common/commonUtils';
import {
  ValidatePermissions,
  findTenant,
  findTenantById,
} from '../../../../Utils/Auth/ValidatePermissions';

import {
  CREATE_MESSAGE,
  UPDATE_MESSAGE,
  ERROR_MESSAGE,
} from '../../../../constants/notificationMessages';
import { listIntegrationTypes } from '../../../Maintenance/IntegrationType/Services/IntegrationTypeService';
/**
 * Add New Integration System main screen
 * @type {React.FC<Props>}
 * @returns {React.ReactElement} The Add New Integration System component.
 */
const AddIntegrationSystem = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const { getParam } = useQueryParams();
  const refId = getParam('refId');
  const { tenantID } = useAuth(); // Get tenantID using Auth Context
  /**
   * Used to handle multiple tabs
   */
  const [selected, setSelected] = React.useState(0);
  const [detailsDataModified, setDetailsDataModified] = React.useState(false);
  const [tenant, setTenant] = React.useState(findTenantById(tenantID));
  const [shouldUpdate, setShouldUpdate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [noRecord, setNoRecord] = React.useState(false);
  const [formData, setFormData] = React.useState({
    TenantId: tenantID,
    TenantName: tenant?.TenantName,
    IntegrationId: '',
    SysType: '',
    Tags: [],
    Active: true,
  });
  const [validateForm, setValidateForm] = React.useState(false);
  const [integrationTypes, setIntegrationTypes] = React.useState([]);
  const [tagOptions, setTagOptions] = React.useState([]);
  const [tagLoading, setTagLoading] = React.useState(false);
  const tagsFilterDebounceRef = React.useRef(null);
  const prevTenantIdRef = React.useRef(formData?.TenantId);
  const requiredFields = ['IntegrationId', 'SysType'];

  const { state } = useLocation();

  const [tempState, setTempState] = React.useState(state);

  // Helper: load all tags for a tenant (used on initial load + when tenant changes)
  async function loadTenantTags(tenantIdToUse) {
    if (!tenantIdToUse) {
      setTagOptions([]);
      return;
    }

    try {
      setTagLoading(true);
      // Empty search text => fetch all tags for this tenant
      const response = await filterIntegrationTags({
        tenantId: tenantIdToUse,
        searchText: '',
        isServiceWorker: false,
      });
      setTagOptions(response?.Payload || []);
    } catch (e) {
      setTagOptions([]);
    } finally {
      setTagLoading(false);
    }
  }

  /**
   * Handle Selected Tab
   * @param {*} e
   */
  const handleSelect = (e) => {
    setSelected(e.selected);
    setShouldUpdate(!shouldUpdate);
  };

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await listIntegrationTypes({
        isServiceWorker: false,
      });
      setIntegrationTypes(response?.integrationTypes);
      setLoading(false);
    })();
  }, []);

  /**
   * Get Integration System Detail from API for edit Integration
   * @param {*} stateData
   */
  const fetchIntegrationSystemData = async (stateData) => {
    setLoading(true);
    try {
      let refObj = base64ToObject(refId);
      const response = await getIntegrationSystem({
        data: refObj,
        isServiceWorker: false,
      });
      const integrationSystemData = response.Payload;
      if (integrationSystemData) {
        // Get tag assignments + tag options for this tenant (edit screen)
        // Keep the loader visible until both are loaded.
        const entityId = `${integrationSystemData?.IntegrationId}|${integrationSystemData?.SysType}|${integrationSystemData?.TenantId}`;

        let assignedTags = [];
        try {
          const [tagsResponse] = await Promise.all([
            getIntegrationTagAssignments({
              tenantId: integrationSystemData?.TenantId,
              entityId,
              isServiceWorker: false,
            }),
            loadTenantTags(integrationSystemData?.TenantId),
          ]);
          assignedTags = tagsResponse?.Payload || [];
        } catch (e) {
          assignedTags = [];
          // If tags/options fail, we still allow opening the page.
          setTagOptions([]);
        }

        setTempState({
          ...stateData,
          dataSet: integrationSystemData,
          edit: true,
        });
        setFormData({
          ...integrationSystemData,
          Tags: assignedTags,
        });
        setTenant(findTenant(integrationSystemData));
        setNoRecord(false);
        if (ValidatePermissions(integrationSystemData)) {
          setDetailsDataModified(true);
        }
      } else {
        setNoRecord(true);
      }
    } catch (error) {
      console.error('Error fetching run set data:', error);
      // Handle error state if needed
      setNoRecord(true);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (tempState || (tenantId && tenantId !== 'new')) {
      const dataSet = state ? state.dataSet : null;
      // Call the async function
      fetchIntegrationSystemData(state);

      if (dataSet) {
        // Rest of the synchronous logic
        setFormData({ ...dataSet });
        setTenant(findTenant(dataSet));
        if (ValidatePermissions(dataSet)) {
          setDetailsDataModified(true);
        } else {
          setDetailsDataModified(false);
        }
        setShouldUpdate(!shouldUpdate);
      }
    } else {
      setDetailsDataModified(true);
    }
  }, [state]);

  // When tenantId (from URL/auth) changes in create mode, hydrate tenant
  // info and load the full tag list for that tenant.
  React.useEffect(() => {
    if (!tempState?.edit && tenantId === 'new') {
      let tempTenant = findTenantById(tenantID);
      setTenant(tempTenant);
      setFormData((prevState) => ({
        ...prevState,
        TenantId: tenantID,
        TenantName: tempTenant?.TenantName,
        // Reset tags on tenant change for new integrations
        Tags: [],
      }));

      // Load all tags for this tenant so dropdown is populated even when
      // the integration has no tags yet.
      loadTenantTags(tenantID);
    }
  }, [tenantID]);

  // Reload tags when tenant changes from TenantDropdown (create mode only)
  React.useEffect(() => {
    if (tenantId !== 'new' || tempState?.edit) return;

    const nextTenantId = formData?.TenantId;
    const prevTenantId = prevTenantIdRef.current;

    if (!nextTenantId || nextTenantId === prevTenantId) return;

    prevTenantIdRef.current = nextTenantId;

    // Clear selected tags, then load tag options for the new tenant
    setFormData((prevState) => ({
      ...prevState,
      Tags: [],
    }));
    loadTenantTags(nextTenantId);
  }, [formData?.TenantId, tenantId, tempState?.edit]);

  React.useEffect(
    () => () => {
      if (tagsFilterDebounceRef.current) {
        clearTimeout(tagsFilterDebounceRef.current);
      }
    },
    [],
  );

  const normalizeTagsValue = (value) => {
    if (!Array.isArray(value)) return [];

    // Convert custom strings into tag objects; keep API objects as-is.
    const normalized = value
      .map((v) => {
        if (typeof v === 'string') {
          return { Name: v };
        }
        if (v && typeof v === 'object') {
          // Some Kendo scenarios may return { Name: '...' } only
          if (typeof v.Name === 'string') return v;
          return null;
        }
        return null;
      })
      .filter(Boolean);

    // Deduplicate by Name (case-insensitive)
    const seen = new Set();
    return normalized.filter((t) => {
      const key = (t.Name || '').trim().toLowerCase();
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const buildTagsAssignmentPayload = () => {
    const tenantIdToUse = formData?.TenantId;
    const entityId = `${formData?.IntegrationId}|${formData?.SysType}|${formData?.TenantId}`;
    const tags = (formData?.Tags || []).map((t) => {
      if (t?.TagId) {
        // Existing tag: send full object as backend expects
        return {
          ...t,
          SourceSystem: 'SmartApps',
        };
      }
      // New tag
      return {
        Name: t?.Name,
        TenantId: tenantIdToUse,
        SourceSystem: 'SmartApps',
        Active: formData?.Active,
      };
    });

    return {
      TenantId: tenantIdToUse,
      EntityType: 'INTEGRATION',
      SourceSystem: 'SmartApps',
      EntityId: entityId,
      Tags: tags,
    };
  };

  const handleTagsFilterChange = (event) => {
    const searchText = event?.filter?.value ?? '';
    const tenantIdToUse = formData?.TenantId;

    if (!tenantIdToUse) {
      setTagOptions([]);
      return;
    }

    if (tagsFilterDebounceRef.current) {
      clearTimeout(tagsFilterDebounceRef.current);
    }

    tagsFilterDebounceRef.current = setTimeout(async () => {
      try {
        setTagLoading(true);
        const response = await filterIntegrationTags({
          tenantId: tenantIdToUse,
          searchText,
          isServiceWorker: false,
        });
        setTagOptions(response?.Payload || []);
      } catch (e) {
        // Swallow filter errors; user can still add custom tags
        setTagOptions([]);
      } finally {
        setTagLoading(false);
      }
    }, 350);
  };

  /**
   * handle Input Change
   * @e object
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;
    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  /**
   * onSave
   * submit the integration system data to api
   */
  const onSave = async ({ isExit = false, isContinue = false }) => {
    setValidateForm(true);
    if (!isFromValid({ formData, requiredFields })) {
      return;
    }

    setLoading(true);
    let notificationMetaData = {};
    // Tags are not part of integration save payload; they are handled
    // separately via /api/Applications/TagAssignments.
    const { Tags, ...integrationPayload } = formData;
    let response;
    let method;

    if (tempState?.edit) {
      method = updateIntegrationSystem;
      notificationMetaData = UPDATE_MESSAGE;
    } else {
      method = createIntegrationSystem;
      notificationMetaData = CREATE_MESSAGE;
    }

    try {
      setValidateForm(false);
      response = await method({
        data: integrationPayload,
        isServiceWorker: false,
      });

      if (!response.IsSuccess) {
        GenerateNotification(
          buildNotification({
            title: response?.Title,
            description: response.Message,
            style: response?.Style,
          }),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
      } else {
        // Assign tags after integration save:
        // - update: always call (even if empty) to allow clearing.
        // - create: call only if there are tags.
        const hasTags = Array.isArray(formData?.Tags) && formData.Tags.length;
        const shouldAssignTags = tempState?.edit ? true : hasTags;

        if (shouldAssignTags) {
          try {
            const tagAssignmentPayload = buildTagsAssignmentPayload();
            const tagResponse = await saveIntegrationTagAssignments({
              data: tagAssignmentPayload,
              isServiceWorker: false,
            });

            if (!tagResponse?.IsSuccess) {
              GenerateNotification(
                buildNotification({
                  title: ERROR_MESSAGE?.title,
                  description:
                    tagResponse?.Message ||
                    'Record saved but tags assignment failed.',
                  style: ERROR_MESSAGE?.style,
                }),
                NOTIFICATION_TYPES.APP,
                EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
              );
            }
          } catch (e) {
            GenerateNotification(
              buildNotification({
                title: ERROR_MESSAGE?.title,
                description: 'Record saved but tags assignment failed.',
                style: ERROR_MESSAGE?.style,
              }),
              NOTIFICATION_TYPES.APP,
              EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
            );
          }
        }

        if (!tempState?.edit) {
          setFormData({
            TenantId: tenantID,
            TenantName: tenant?.TenantName,
            IntegrationId: '',
            SysType: '',
            Tags: [],
            Active: true,
          });
        }

        GenerateNotification(
          buildNotification(notificationMetaData),
          NOTIFICATION_TYPES.APP,
          EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
        );
        // Update tempState to trigger rerender
        setTempState({
          ...tempState,
          dataSet: response?.Payload,
        });
        if (isContinue) {
          const payloadData = response?.Payload;
          const refId = objectToBase64({
            IntegrationId: payloadData?.IntegrationId,
            SysType: payloadData?.SysType,
            TenantId: payloadData?.TenantId,
          });
          navigate(
            `/integration/system/${payloadData?.TenantId}?refId=${refId}`,
            {
              state: {
                edit: true,
                dataSet: payloadData,
              },
            },
          );
        }
        if (isExit) {
          navigate('/integration/systems');
        }
      }
    } catch (error) {
      GenerateNotification(
        buildNotification(ERROR_MESSAGE),
        NOTIFICATION_TYPES.APP,
        EVENTS_DATA_TYPES.APPLICATION_NOTIFICATION,
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * onExit
   * back to integration system listing screen
   */
  const onExit = () => {
    navigate('/integration/systems');
  };

  const handleSaveSplit = (e) => {
    const { itemIndex } = e;
    switch (itemIndex) {
      case 0: {
        onSave({ isExit: true });
        break;
      }
      case 1:
        onSave({ isContinue: true });
      default: {
      }
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <div className="container mx-auto">
        <React.Suspense fallback={<Loader />}>
          {loading && <Loader />}
          {noRecord ? (
            <NoRecordFound
              recordNotFoundText={'No Data found'}
              onExit={onExit}
            />
          ) : (
            <div
              className={`add-rf-profile-group main-page-section add-page-section`}
            >
              <Card className="tophead">
                <CardHeader>
                  <div className="action-buttons-container">
                    <Link
                      className="action-icon-wrapper !k-d-block"
                      to={`/integration/systems`}
                    >
                      <FontAwesomeIcon
                        className="k-mr-2 k-pt-3"
                        icon={faArrowLeft}
                      />
                    </Link>
                    <div>
                      <p className="display-text k-mb-0">
                        Integration System: {formData?.IntegrationId}
                      </p>
                    </div>
                  </div>
                </CardHeader>
              </Card>
              <Card className="k-mt-3 detail-card">
                <CardHeader>
                  <h6 className="card-title-secondary">Details</h6>
                </CardHeader>
                <CardBody className="detail-card-body">
                  <div>
                    <Form
                      onSubmit={onSave}
                      render={(formRenderProps) => (
                        <div className="card-content">
                          <div className="k-d-flex single-field-row">
                            <div
                              className={`field-wrapper ${isValidate(validateForm, formData?.TenantName) ? 'field-invalid' : 'field-valid'}`}
                            >
                              <TenantDropdown
                                textField="TenantName"
                                label="Tenant"
                                dataItemKey="TenantId"
                                className="tenant-dropdown k-align-self-end"
                                tenant={tenant}
                                setTenant={setTenant}
                                defaultItem={{
                                  TenantName: 'Select Tenant ...',
                                  disabled: true,
                                }}
                                editMode={tempState?.edit ? 'ReadOnly' : ''}
                                filterable={true}
                                tenantFormId="TenantId"
                                tenantFormName="TenantName"
                                setFormData={setFormData}
                                requiredField={true}
                              />
                              {isValidate(
                                validateForm,
                                formData?.TenantName,
                              ) && <Error>This field cannot be empty</Error>}
                            </div>
                          </div>
                          <div className="k-d-flex single-field-row">
                            <div
                              className={`field-wrapper ${isValidate(validateForm, formData?.IntegrationId) ? 'field-invalid' : 'field-valid'}`}
                            >
                              <Field
                                key={'IntegrationId'}
                                id={'IntegrationId'}
                                name={'IntegrationId'}
                                data={formData?.IntegrationId}
                                onChange={handleInputChange}
                                label={'Id'}
                                type={'text'}
                                component={TextInput}
                                renderTypes={tempState?.edit ? 'ReadOnly' : ''}
                                optional={false}
                                requiredField={true}
                              />
                              {isValidate(
                                validateForm,
                                formData?.IntegrationId,
                              ) && <Error>This field cannot be empty</Error>}
                            </div>
                            <div
                              className={`field-wrapper ${isValidate(validateForm, formData?.SysType) ? 'field-invalid' : 'field-valid'}`}
                            >
                              <DropDownInput
                                label="System Type"
                                textField="SysType"
                                dataItemKey="SysType"
                                name={'SysType'}
                                dataSet={integrationTypes}
                                value={
                                  formData?.SysType
                                    ? {
                                        SysType: formData?.SysType,
                                      }
                                    : null
                                }
                                handleChange={(e) => {
                                  setFormData((prevState) => ({
                                    ...prevState,
                                    SysType: e.value?.SysType,
                                  }));
                                }}
                                defaultItem={{
                                  SysType: 'Select Type ...',
                                }}
                                filterable={true}
                                renderTypes={tempState?.edit ? 'ReadOnly' : ''}
                                requiredField={true}
                              />
                              {isValidate(validateForm, formData?.SysType) && (
                                <Error>This field cannot be empty</Error>
                              )}
                            </div>
                          </div>
                          <div className="k-d-flex single-field-row">
                            <div className="field-wrapper">
                              <FormMultiSelect
                                id="Tags"
                                name="Tags"
                                label={'Tags'}
                                placeholder="Type to search tags..."
                                textField="Name"
                                dataItemKey="Name"
                                dataSet={tagOptions}
                                value={formData?.Tags || []}
                                filterable={true}
                                onFilterChange={handleTagsFilterChange}
                                allowCustom={true}
                                disabled={!detailsDataModified}
                                onChange={(e) => {
                                  const normalized = normalizeTagsValue(
                                    e?.target?.value,
                                  );
                                  setFormData((prev) => ({
                                    ...prev,
                                    Tags: normalized,
                                  }));
                                }}
                              />
                            </div>
                          </div>
                          <div className="single-field-row k-mt-4 single-row-60">
                            <div className={`field-wrapper`}>
                              <Field
                                name="Active"
                                component={InputSwitch}
                                label="Active"
                                onLabel="Yes"
                                offLabel="No"
                                id="Active"
                                checked={formData?.Active}
                                onChange={handleInputChange}
                                disabled={!detailsDataModified}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    />
                  </div>
                </CardBody>
              </Card>
              {detailsDataModified && (
                <Card className="k-mt-3">
                  <CardHeader>
                    <div className="k-d-flex single-field-row button-form-wrapper k-action-buttons !k-px-0">
                      <Button
                        icon={'cancel'}
                        onClick={() => onExit()}
                        type="button"
                      >
                        Cancel
                      </Button>
                      <SplitButton
                        text="save"
                        themeColor={'primary'}
                        onButtonClick={() =>
                          onSave({ isExit: false, isContinue: true })
                        }
                        onItemClick={handleSaveSplit}
                      >
                        <SplitButtonItem text="Save & Close" />
                      </SplitButton>
                    </div>
                  </CardHeader>
                </Card>
              )}
              {tempState?.edit && (
                <Card className="k-mt-3 detail-grid-section">
                  <CardBody className="!k-px-0">
                    <DesktopLayout
                      handleSelect={handleSelect}
                      selected={selected}
                      detailsDataModified={detailsDataModified}
                      state={tempState}
                      shouldUpdate={shouldUpdate}
                      setShouldUpdate={setShouldUpdate}
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          )}
        </React.Suspense>
      </div>
    </ErrorBoundary>
  );
};
export default AddIntegrationSystem;
